import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import PromptsManagerPage from "pages/dashboard/admin/prompts/PromptsManagerPage";
import AllFilesPage from "pages/dashboard/admin/AllFilesPage";
import TeamPage from "pages/dashboard/admin/TeamPage";
import UsagePage from "pages/dashboard/admin/UsagePage";
import AdminOnboarding from "pages/dashboard/admin/AdminOnboarding";
import AdminOnboardingDialog from "components/dashboard/AdminOnboardingDialog";
import Settings from "components/dashboard/Settings";
import ChartMindPage from "pages/dashboard/professional/ChartMindPage";
import MyChartsPage from "pages/dashboard/professional/MyChartsPage";
import PeerViewPage from "pages/dashboard/professional/PeerViewPage";
import { useAuth } from "hooks/useAuth";
import DashboardLayout from "components/dashboard/DashboardLayout";
import { PromptsManagerProvider } from "context/PromptsManager";
import { ChartMindSessionProvider } from "context/ChartMindSession";
import { db } from "services/firebase";
import HomePage from "pages/dashboard/HomePage";

/**
 * AdminRoutes - Routes for ChartMind Manager (admin role)
 *
 * Flow:
 * - No subscription AND not beta admin → Full-screen onboarding (purchase seats)
 * - Has subscription OR is beta admin → Dashboard with Prompts + Organization
 */
const AdminRoutes = () => {
  const { user, userData, subscription, subscriptionData, isBetaAdmin, organizationLoading } =
    useAuth();
  const location = useLocation();
  const [showAdminOnboarding, setShowAdminOnboarding] = useState(false);
  const path = location.pathname;
  const isChartMindSessionList = /\/chartmind\/sessions\/?$/.test(path);
  const chartMindFullWidth =
    (path.includes("/chartmind") || path.includes("/peerview")) &&
    !isChartMindSessionList;
  const hasActiveSubscription = subscriptionData?.status === "active";

  // Quick welcome dialog on first entry (same gate as patient/professional).
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user?.uid) return;
      try {
        const data = (await db.collection("users").doc(user.uid).get()).data();
        if (data?.role === "admin" && !data?.hideOnboardingDialog) {
          setShowAdminOnboarding(true);
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
      }
    };
    checkOnboardingStatus();
  }, [user]);

  // Wait for org to load before deciding — prevents beta admins seeing onboarding flash on refresh.
  if (organizationLoading) return null;

  // [BETA-KIJABE-ADMIN] Beta admin orgs skip subscription requirement entirely.
  if (!hasActiveSubscription && !isBetaAdmin) {
    return <AdminOnboarding />;
  }

  // Normal dashboard with sidebar
  return (
    <DashboardLayout fullWidth={chartMindFullWidth}>
      <AdminOnboardingDialog
        open={showAdminOnboarding}
        onClose={() => setShowAdminOnboarding(false)}
        userId={user?.uid}
        userData={userData}
      />

      <PromptsManagerProvider>
        <ChartMindSessionProvider>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/prompts" element={<PromptsManagerPage />} />
            <Route path="/files" element={<AllFilesPage />} />
            <Route path="/diskusage" element={<Navigate to="/dashboard/files" replace />} />
            <Route path="/team" element={<TeamPage />} />
            {/* Was hidden from beta admin orgs because the page measured tokens and beta orgs
                had no token tracking. It now reads the ChartMind step counters, which every
                org writes — beta included, since those counters are written on step arrival
                whether or not the session persists. Beta admins were the orgs most in need of
                it and the only ones who could not reach it. */}
            <Route path="/usage" element={<UsagePage />} />
            <Route
              path="/settings"
              element={<Settings uid={user.uid} subscription={subscription} />}
            />
            <Route
              path="/chartmind"
              element={
                <ChartMindPage key={location.state?.key || location.pathname} />
              }
            />
            <Route path="/chartmind/sessions" element={<MyChartsPage />} />
            <Route path="/peerview" element={<PeerViewPage />} />
            <Route
              path="/chartmind/:sessionId"
              element={
                <ChartMindPage key={location.state?.key || location.pathname} />
              }
            />
            <Route path="*" element={<Navigate to="/dashboard/prompts" />} />
          </Routes>
        </ChartMindSessionProvider>
      </PromptsManagerProvider>
    </DashboardLayout>
  );
};

export default AdminRoutes;
